/* eslint-disable prettier/prettier */
import _ from 'lodash';
import c from 'classnames';
import React, { createRef } from 'react';
import { decorate, runInAction, observable, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Icon } from 'semantic-ui-react';
import { ChatbotHeaderIcon } from './chatbot/ChatbotIcon';
import AssistantIcon from './chatbot/AssistantIcon';
import Bubble from './chatbot/Bubble';
import chatBotStore from './chatbot/ChatBotStore';

class ChatBot extends React.Component {
    constructor() {
        super();
        
        runInAction(() => {
            this.waitingAssistantResponse = false;
            this.currentSessionId = null;
            this.ignoredSessionIds = [];
            this.assistantPendingMessage = '';
            this.chatHistory = [];
            this.chatbotReady = false;
            this.isClosing = false;
            this.isDragging = false;
        })
        this.closeTimer = null;
    }

    conversationMainRef = createRef();

    get isShowing() {
        return chatBotStore.isShowing;
    }

    render() {
        if (!this.isShowing && !this.isClosing) {
            return (
                <button className='chatbot-trigger' onClick={this.toggleChat} aria-label='Open chat'>
                    <AssistantIcon />
                </button>
            );
        }

        return (
            <div
                className={c('chatbot-panel', { 'chatbot-panel-entering': this.isShowing, 'chatbot-panel-exiting': this.isClosing })}
                style={{ width: chatBotStore.panelWidth }}
            >
                {/* Drag handle for resizing */}
                <div
                    className={c('chatbot-drag-handle', { dragging: this.isDragging })}
                    onMouseDown={this.handleDragStart}
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize chat panel"
                    tabIndex={0}
                    onKeyDown={this.handleDragKeyDown}
                />
                <div className='chatbot-header'>
                    <span className='chatbot-header-title'>
                        <ChatbotHeaderIcon />
                        RACE Assistant
                    </span>
                    <button className='chatbot-header-close' onClick={this.toggleChat} aria-label='Close chat'>
                        <Icon name='angle right' />
                    </button>
                </div>
                <div className='chatbot-conversations' ref={this.conversationMainRef}>
                    <Bubble theRole='assistant'>Hi, I&apos;m the RACE Assistant. I can help with questions about workspaces, data, and using the platform. What can I help you with?</Bubble>
                    {  _.map(this.chatHistory, (chat, index) => (
                        <Bubble theRole={chat.role} key={index}>
                            { chat.content }
                        </Bubble>
                    ))  }
                    <Bubble 
                        theRole='assistant'
                        isPending={this.waitingAssistantResponse}
                        className={c('bubble', 'assistant', { hidden: !this.waitingAssistantResponse })}
                    >
                        { this.assistantPendingMessage }
                    </Bubble>
                    { 
                        !this.waitingAssistantResponse && this.chatHistory.length ?
                        <div
                            className='reset-conversation'
                            onClick={this.resetConversation}
                        >
                            <Icon name='redo' style={{ margin: 0, fontSize: '0.85em' }} />
                            Reset Conversation
                        </div> : <></>
                    }
                </div>
                <form className='chatbot-input-area' onSubmit={this.submitMessage}>
                    <input type='text' name='message' placeholder='Type your message...' autoComplete='off' />
                    <button
                        type={ this.waitingAssistantResponse ? 'button' : 'submit' }
                        className={c('chatbot-send-btn', { waiting: this.waitingAssistantResponse })}
                        onClick={ this.waitingAssistantResponse ? this.interruptResp : undefined }
                        aria-label={ this.waitingAssistantResponse ? 'Stop response' : 'Send message' }
                    >
                        <Icon name={ this.waitingAssistantResponse ? 'stop' : 'send' } />
                    </button>
                </form>
            </div>
        )
    }

    // ── Drag-to-resize handlers ──
    handleDragStart = (e) => {
        e.preventDefault();
        runInAction(() => { this.isDragging = true; });
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', this.handleDragMove);
        document.addEventListener('mouseup', this.handleDragEnd);
    };

    handleDragMove = (e) => {
        const newWidth = window.innerWidth - e.clientX;
        chatBotStore.setPanelWidth(newWidth);
    };

    handleDragEnd = () => {
        runInAction(() => { this.isDragging = false; });
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', this.handleDragMove);
        document.removeEventListener('mouseup', this.handleDragEnd);
    };

    handleDragKeyDown = (e) => {
        const step = 20;
        if (e.key === 'ArrowLeft') {
            chatBotStore.setPanelWidth(chatBotStore.panelWidth + step);
        } else if (e.key === 'ArrowRight') {
            chatBotStore.setPanelWidth(chatBotStore.panelWidth - step);
        }
    };

    autoScroll = () => {
        if(this.conversationMainRef.current) {
            this.conversationMainRef.current.scrollTo({
                top: this.conversationMainRef.current.scrollHeight,
                behavior: 'smooth'
            })
        }
    }

    submitMessage = (event) => {
        event.preventDefault();
        const message = event.target.message.value;

        if(!message || this.waitingAssistantResponse || !this.wsClient || !this.chatbotReady) return;

        this.startResponse();
        this.wsClient.send(JSON.stringify({ action: "Completions", prompt: message, history: this.chatHistory }));
        this.addNewHistory('user', message)
        event.target.message.value = '';
    }
    
    interruptResp = () => {
        this.ignoredSessionIds.push(this.currentSessionId);
        this.currentSessionId = null;
        this.updateAssistantResponse(`${this.assistantPendingMessage  }[ Interruped Conversation ]`, true);
    }

    addNewHistory = (role, content) => {
        this.chatHistory.push({ role, content });
        this.autoScroll();
    }

    resetChatbotState = () => {
        this.chatbotReady = false;
        this.wsClient = null;
    }

    toggleChat = () => {
        if (this.isShowing) {
            // Start close animation, then actually close
            runInAction(() => { this.isClosing = true; });
            chatBotStore.toggleChat();
            this.closeTimer = setTimeout(() => {
                runInAction(() => { this.isClosing = false; });
                // eslint-disable-next-line no-unused-expressions
                this.wsClient && this.wsClient.close();
            }, 400);
        } else {
            if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null; }
            runInAction(() => { this.isClosing = false; });
            chatBotStore.toggleChat();
            // eslint-disable-next-line no-unused-expressions
            this.wsClient && this.wsClient.close();
            if(!process.env.REACT_APP_CHATBOT_WS_URL) {
                console.error("ws url not set");
                this.resetChatbotState();
                return;
            }

            this.wsClient = new WebSocket(process.env.REACT_APP_CHATBOT_WS_URL);
           
            this.wsClient.onerror = () => {
                console.error("load ws error")
                this.resetChatbotState();
            }

            this.wsClient.onopen = () => {
                runInAction(() => {
                    this.chatbotReady = true;
                });
            }

            this.wsClient.onmessage = event => {
                const data = JSON.parse(event.data);
                const { content, isFinished, sessionId, completed } = data;
                if(this.ignoredSessionIds.includes(sessionId) || completed) return;
                if(!this.sessionId) {
                    runInAction(() => {
                        this.currentSessionId = sessionId;
                    })
                }
                this.updateAssistantResponse(content, isFinished);
            }

            this.wsClient.onclose = () => this.resetChatbotState;
        }
    }

    startResponse = () => {
        this.waitingAssistantResponse = true;
    }

    updateAssistantResponse = (message, isFinished) => {
        if(isFinished) {
            this.waitingAssistantResponse = false;
            this.assistantPendingMessage = '';
            this.addNewHistory('assistant', message || 'Sorry, something went wrong and no response was received. Please try again.');
        } else {
            this.assistantPendingMessage = message;
            this.autoScroll();
        }
    }

    resetConversation = () => {
        this.chatHistory = [];
    }
}

decorate(ChatBot, {
    waitingAssistantResponse: observable,
    assistantPendingMessage: observable,
    chatHistory: observable,
    wsClient: observable,
    chatbotReady: observable,
    currentSessionId: observable,
    ignoredSessionIds: observable,
    isClosing: observable,
    isDragging: observable,
    submitMessage: action,
    interruptResp: action,
    addNewHistory: action,
    startResponse: action,
    updateAssistantResponse: action,
    toggleChat: action,
    resetChatbotState: action,
    resetConversation: action,
});
  
export default inject()(withRouter(observer(ChatBot)));
